/* eslint-disable no-console */
/* eslint-disable import/prefer-default-export */
import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';

/**
 * Save a chat message to the database
 * @param {object} messageData - Message data to save
 * @returns {Promise} API response
 */
export const sendMessage = async (messageData) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.CHAT.SAVE_MESSAGE}`, messageData);
  } catch (error) {
    console.error('Error saving chat message:', error);
    throw error;
  }
};

/**
 * Get chat messages for a room
 * @param {string} roomName - Room/channel name
 * @returns {Promise} API response with messages
 */
export const getChatMessages = async (params) => {
  const { roomName, supplierId, moduleRefId, pageSize, page, searchText, module } = params;
  try {
    let query = `?supplierId=${supplierId}&moduleRefId=${moduleRefId}&pageSize=${pageSize}&page=${page}`;
    if (searchText) {
      query += `&searchText=${searchText}`;
    }
    if (module) {
      query += `&module=${encodeURIComponent(module)}`;
    }
    return await apiClient.get(`${apiEndpoint}${API.CHAT.GET_MESSAGES}/${roomName}${query}`);
  } catch (error) {
    console.error('Error getting chat messages:', error);
    throw error;
  }
};

/**
 * Mark messages as read
 * @param {string} roomName - Room/channel name
 * @param {string} module - Module type (e.g., "Samples" or "Costing")
 * @returns {Promise} API response
 */
export const markMessagesAsRead = async (roomName, moduleRefId) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.CHAT.MARK_READ}`, { roomName, moduleRefId });
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

/**
 * Get unread message count for a module
 * @param {string} moduleRefId - Module reference ID (e.g., Sample ID)
 * @param {string} module - Module type (e.g., "Samples" or "Costing")
 * @returns {Promise} API response with count
 */
export const getUnreadMessageCount = async (moduleRefId, module) => {
  try {
    const query = module ? `?module=${encodeURIComponent(module)}` : '';
    return await apiClient.get(`${apiEndpoint}${API.CHAT.UNREAD_COUNT}/${moduleRefId}${query}`);
  } catch (error) {
    console.error('Error getting unread message count:', error);
    throw error;
  }
};

/**
 * Delete a chat message
 * @param {string} messageId - Message ID to delete
 * @returns {Promise} API response
 */
export const deleteMessage = async (messageData) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.CHAT.DELETE_MESSAGE}/${messageData.serial}`, {
      params: { module: messageData.module },
    });
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * Edit a chat message
 * @param {string} messageId - Message ID to edit
 * @param {string} message - New message content
 * @returns {Promise} API response
 */
export const editMessage = async (messageId, messageData) => {
  try {
    return await apiClient.put(`${apiEndpoint}${API.CHAT.EDIT_MESSAGE}/${messageId}`, { ...messageData });
  } catch (error) {
    console.error('Error editing message:', error);
    throw error;
  }
};

/**
 * Get unread messages for module/devOrder to prime UI state
 * @param {{ module: string, devOrderId?: string }} params
 */
export const getUnreadMessages = async ({ module, devOrderId }) => {
  try {
    const qs = `?module=${encodeURIComponent(module)}${devOrderId ? `&devOrderId=${encodeURIComponent(devOrderId)}` : ''}`;
    return await apiClient.get(`${apiEndpoint}${API.CHAT.GET_UNREAD_MESSAGES}${qs}`);
  } catch (error) {
    console.error('Error getting unread messages:', error);
    throw error;
  }
};

/**
 * Get chat participants for a room
 * @param {string} roomName - Room/channel name
 * @returns {Promise} API response with participants
 */
export const getChatParticipants = async (roomName) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.CHAT.GET_PARTICIPANTS}/${roomName}`);
  } catch (error) {
    console.error('Error getting chat participants:', error);
    throw error;
  }
};

/**
 * Get users list for mentions (scoped by dev order, module and optionally
 * the supplier the internal user is currently chatting with).
 * @param {object} params - { devOrderId, module, mainModule, supplierId }
 */
export const getUsersList = async ({ devOrderId, module, mainModule = 'products', supplierId } = {}) => {
  try {
    const params = new URLSearchParams();
    if (devOrderId) params.set('devOrderId', devOrderId);
    if (module) params.set('module', module);
    params.set('mainModule', mainModule);
    if (supplierId) params.set('supplierId', supplierId);
    return await apiClient.get(`${apiEndpoint}${API.CHAT.USERS_LIST}?${params.toString()}`);
  } catch (error) {
    console.error('Error getting chat users list:', error);
    throw error;
  }
};
